import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

// Define the type for a single channel from the API
interface Channel {
  channel_name: string;
}

// The Team type is a dynamic string from the API
export type Team = string;

interface ChannelNavBarProps {
  currentTeam: Team;
  setCurrentTeam: (team: Team) => void;
  onChannelsFetched?: (channels: Team[]) => void; // New: Callback to notify parent of fetched data
}

export const ChannelNavBar: React.FC<ChannelNavBarProps> = ({ currentTeam, setCurrentTeam, onChannelsFetched }) => {
  const [channels, setChannels] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const response = await fetch('https://coliving-agent.onrender.com/coliving-ai-os/chat-analysis/database/api/channels');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const result = await response.json();
        if (result.status === 'success' && Array.isArray(result.data)) {
          const channelNames = result.data.map((channel: Channel) => channel.channel_name);
          setChannels(channelNames);
          // If the callback is provided, call it with the fetched channel names
          if (onChannelsFetched) {
            onChannelsFetched(channelNames);
          }
        } else {
          throw new Error('Invalid data format from API');
        }
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();
  }, [onChannelsFetched]); // useEffect dependency array includes the new prop

  if (error) {
    return <div className="text-center py-4 text-red-500">Error: Could not load channels.</div>;
  }
  
  // Render a loading or placeholder state while fetching
  if (loading || channels.length === 0) {
      return (
        <header className="flex justify-center py-3 bg-white border-b border-gray-200 sticky top-0 z-10">
            <nav className="flex items-center space-x-2 bg-gray-100 p-1.5 rounded-full shadow-inner">
                <div className="px-6 py-2 rounded-full text-sm font-semibold text-gray-400">Loading...</div>
            </nav>
        </header>
      )
  }

  return (
    <header className="flex justify-center py-3 bg-white border-b border-gray-200 sticky top-0 z-10">
      <nav className="flex items-center space-x-2 bg-gray-100 p-1.5 rounded-full shadow-inner">
        {channels.map((team) => (
          <button
            key={team}
            onClick={() => setCurrentTeam(team)}
            className={clsx(
              'px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200 ease-in-out transform',
              {
                'bg-white text-blue-600 shadow-sm scale-105': currentTeam === team,
                'text-gray-500 hover:text-gray-800': currentTeam !== team,
              }
            )}
            aria-current={currentTeam === team ? 'page' : undefined}
          >
            {team}
          </button>
        ))}
      </nav>
    </header>
  );
};